import { GridColDef } from '@mui/x-data-grid-pro';
import { RulesKeys } from '../../../api/rules-engine-api';
import { useMemo } from 'react';
import { Policy, RuleModel, RulesEngineActionLog } from '../../../../types';
import { actionOptions } from './action-options';
import { useQueryClient } from '@tanstack/react-query';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../shared/shared-toolbar-latest';

import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import Check from '@mui/icons-material/Check';
import Refresh from '@mui/icons-material/Refresh';
import History from '@mui/icons-material/History';

interface RulesEngineActionLogsGridProps {
  data: RulesEngineActionLog[];
  isLoading: boolean;
  policies: Policy[];
  rules: RuleModel[];
}

export default function RulesEngineActionLogsGrid({
  data,
  isLoading,
  policies,
  rules,
}: RulesEngineActionLogsGridProps) {
  const queryClient = useQueryClient();

  const rulesMap = useMemo(() => {
    if (!rules) return new Map();
    return new Map(rules.map((rule) => [rule.id, rule]));
  }, [rules]);

  function CustomToolbar() {
    return (
      <SharedToolbar noColumn>
        <Tooltip arrow title="Refresh">
          <IconButton
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [RulesKeys.RunLogActions],
              });
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </SharedToolbar>
    );
  }

  const columns: GridColDef<RulesEngineActionLog>[] = useMemo(
    () => [
      {
        field: 'createdDate',
        headerName: 'Created Date',
        width: 185,
        display: 'flex',
        type: 'dateTime',
        valueGetter: (params: number) => {
          return new Date(params);
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
        sortDirection: 'desc',
      },
      {
        field: 'ruleId',
        headerName: 'Rule Name/ID',
        flex: 1,
        display: 'flex',
        type: 'string',
        valueGetter: (_value, row) => {
          const rule = rulesMap.get(row.ruleId);
          return rule?.basicInfo.ruleName ?? String(row.ruleId);
        },
        renderCell: (params) => {
          const rule = rulesMap.get(params.row.ruleId);
          if (isLoading) {
            return <Skeleton width="80%" />;
          } else if (!rule) {
            return String(params.row.ruleId);
          } else {
            return rule.basicInfo.ruleName;
          }
        },
      },
      {
        field: 'actionType',
        headerName: 'Action Type',
        width: 185,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: actionOptions.map((option) => {
          return { label: option.displayValue, value: option.value };
        }),
        renderCell: (params) => {
          const displayValue = actionOptions.find(
            (option) => option.value === params.row.actionType
          )?.displayValue;
          return (
            <Chip
              label={displayValue}
              size="small"
              sx={{ width: 'fit-content' }}
            />
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: [
          { label: 'success', value: 'Success' },
          { label: 'failed', value: 'Failed' },
        ],
        renderCell: (params) => {
          const status = params.row.status;

          let chip = (
            <Chip
              variant="outlined"
              icon={<Check />}
              label={`${status}`}
              color={'success'}
              size="small"
            />
          );
          if (status === 'failed') {
            chip = (
              <Chip
                variant="outlined"
                icon={<CancelOutlined />}
                label={`${status}`}
                color={'error'}
                size="small"
              />
            );
          }
          return chip;
        },
      },
      {
        field: 'target',
        headerName: 'Target',
        display: 'flex',
        flex: 1,
        valueGetter: (params: string) => {
          if (Number.isNaN(parseInt(params))) {
            return params;
          } else {
            return (
              policies.find((policy) => policy.id === parseInt(params))
                ?.policyName || params
            );
          }
        },
      },
      {
        field: 'actionString',
        display: 'flex',
        headerName: 'Description',
        flex: 2,
        valueGetter: (params: string) => {
          return params;
        },
      },
    ],
    [policies, rulesMap, isLoading]
  );

  return (
    <SharedDataGridClient
      slots={{
        toolbar: CustomToolbar,
        ...createGridOverlays('Logs', <History />, 'In the last 30 days'),
      }}
      sx={{
        mt: 1,
        height: 600,
        '.MuiDataGrid-cell': {
          py: 1,
        },
      }}
      getRowId={(row) =>
        row.id ? row.id : `${row.createdDate}-${row.actionType}`
      }
      rows={data || []}
      columns={columns}
      loading={isLoading}
      defaultGridState={{
        sorting: {
          sortModel: [{ field: 'createdDate', sort: 'desc' }],
        },
      }}
      disableRowSelectionOnClick
      density="compact"
      getRowHeight={() => 'auto'}
    />
  );
}
